import React, { useEffect } from 'react';
import { t } from '@lingui/macro';
import { useQueryClient } from '@tanstack/react-query';

import ArrowLeft from '@rover/icons/svg/arrow-left.svg';
import { Box, Button, Flex, Heading, Text } from '@rover/kibble/core';
import ReviewReply from '@rover/react-lib/src/components/modals/Review/ReviewReply';
import { useApiReviewsAutomaticrecordresponseDetailDestroy } from '@rover/rsdk/src/apiClient/latest/api-reviews-automaticrecordresponse-detail-destroy';
import { useApiReviewsRatingresponseDetailDestroy } from '@rover/rsdk/src/apiClient/latest/api-reviews-ratingresponse-detail-destroy';
import { AutomaticRecordTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/automaticRecordTypeEnum';
import type { ReviewDispatch } from '@rover/rsdk/src/apiClient/latest/model/reviewDispatch';

import { getOpkFromReviewUrl } from '../../../utils/utils';

type Props = {
  review: ReviewDispatch;
  sitterAvatar: string;
  onBack: () => void;
  onClose: () => void;
  sitterOpk: string;
};

const ArrowIcon = (): JSX.Element => <ArrowLeft width="24px" height="24px" />;

const DeleteForm = ({
  review,
  onBack,
  onClose,
  sitterAvatar,
  sitterOpk,
}: Props): JSX.Element | null => {
  const queryClient = useQueryClient();
  const { mutate: reviewsRatingResponseMutate } = useApiReviewsRatingresponseDetailDestroy();
  const { mutate: automaticRecordResponseMutate } =
    useApiReviewsAutomaticrecordresponseDetailDestroy();

  const ref = React.useRef<HTMLDivElement>(null);

  const handleDelete = (): void => {
    const opk = getOpkFromReviewUrl(review.url);
    const mutate =
      review.type in AutomaticRecordTypeEnum
        ? automaticRecordResponseMutate
        : reviewsRatingResponseMutate;
    mutate(
      { reviewOpk: opk },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            predicate: (query) => {
              const keyStr = JSON.stringify(query.queryKey);
              return keyStr.includes(`/api/v7/people/${sitterOpk}/reviews/`);
            },
            refetchType: 'all',
          });
          onBack();
        },
      }
    );
  };

  useEffect(() => {
    ref.current?.focus();
  }, []);

  if (!('response' in review)) return null;
  return (
    <Box ref={ref} webProps={{ tabIndex: -1 }} aria-label={t`Confirmation dialog`}>
      <Flex mb="4x">
        <Box flexShrink="0">
          <Button
            alignSelf="flex-start"
            variant="flat"
            size="small"
            icon={ArrowIcon}
            onClick={onBack}
            aria-label={`${t`Go back to reviews list`}`}
            data-testid="back-arrow"
            p="0x"
          />
        </Box>
        <Heading size="300" mb="0x" ml="2x">
          {t`Delete Reply`}
        </Heading>
      </Flex>
      <Text>{t`Are you sure you want to delete this reply?`}</Text>
      {review.response && (
        <Box pt="6x" sx={{ paddingBottom: '40px' }}>
          <ReviewReply reply={review.response} avatarUrl={sitterAvatar} />
        </Box>
      )}
      <Box mx="1x" pb="1x">
        <Button fullWidth onClick={handleDelete} mb="4x">
          <Text textColor="error">{t`Delete reply`}</Text>
        </Button>
        <Button fullWidth onClick={onClose} sx={{ paddingTop: '10px' }}>
          {t`Close`}
        </Button>
      </Box>
    </Box>
  );
};

export default DeleteForm;
